"use client";

import { useAuth } from "@/components/AuthContext";
import { AuthForm } from "@/components/AuthForm";
import { BrandMark, BrandMascot } from "@/components/brand";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import { OptionCard } from "@/components/onboarding/quiz/OptionCard";
import { ContinueButton } from "@/components/onboarding/quiz/QuizLayout";
import languages from "@/data/languages.json";
import { useRouter } from "@/hooks/useSchoolRouter";
import { trackTutorEvent } from "@/lib/analytics/tutor-events";
import { createLogger } from "@/lib/logger";
import type { TutorStudioCourse } from "@/lib/tutor-studio/curriculumRegistry";
import { registerTutorAccount } from "@/lib/tutor-studio/registerTutorAccount";
import { Check, ChevronLeft, Loader2, Mic2 } from "lucide-react";
import Image from "next/image";
import { usePostHog } from "posthog-js/react";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TutorDemoCompletionReason, TutorSpeakingDemo } from "./TutorSpeakingDemo";
import {
  TUTOR_CEFR_OPTIONS,
  TUTOR_ONBOARDING_STEP_IDS,
  TutorOnboardingAnswers,
  TutorOnboardingState,
  normalizeTutorOnboardingSource,
  parseStoredTutorOnboardingState,
} from "./experience";

const logger = createLogger("tutor-onboarding");
const STORAGE_KEY = "chickytutor_tutor_onboarding_v3";
const LANGUAGE_OPTIONS = languages as Record<string, string>;
const POPULAR_TUTOR_LANGUAGES = [
  { name: "Spanish (Spain)", shortName: "Spanish", code: "es-ES", flagSrc: "/flags/es.svg" },
  { name: "French", shortName: "French", code: "fr-FR", flagSrc: "/flags/fr.svg" },
  { name: "Dutch", shortName: "Dutch", code: "nl-NL", flagSrc: "/flags/nl.svg" },
  { name: "German", shortName: "German", code: "de-DE", flagSrc: "/flags/de.svg" },
  { name: "Italian", shortName: "Italian", code: "it-IT", flagSrc: "/flags/it.svg" },
  {
    name: "English (United States)",
    shortName: "English",
    code: "en-US",
    flagSrc: "/flags/us.svg",
  },
] as const;

function TutorOnboardingShell({
  children,
  currentStep,
  onBack,
  wide = false,
}: {
  children: ReactNode;
  currentStep: number;
  onBack?: () => void;
  wide?: boolean;
}) {
  const progress = ((currentStep + 1) / TUTOR_ONBOARDING_STEP_IDS.length) * 100;

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[#0F0F14] text-white">
      <div className="pointer-events-none absolute inset-0 opacity-80 [background:radial-gradient(circle_at_80%_12%,rgba(253,149,79,0.14),transparent_30%),radial-gradient(circle_at_15%_88%,rgba(255,255,255,0.04),transparent_24%)]" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0F0F14]/92 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          {currentStep > 0 && onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="-ml-2 inline-flex min-h-11 min-w-16 items-center gap-1 rounded-lg p-2 text-sm font-semibold text-white/55 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
              aria-label="Go back"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              <span className="hidden sm:inline">Back</span>
            </button>
          ) : (
            <div className="w-16" />
          )}

          <div className="flex items-center gap-2.5">
            <BrandMark size={34} className="h-[34px] w-[34px] object-contain" alt="" priority />
            <div>
              <div className="text-sm font-extrabold">ChickyTutor</div>
              <div className="text-[11px] font-semibold text-white/40">For tutors</div>
            </div>
          </div>

          <div className="w-16 text-right text-xs font-bold text-white/40">
            {currentStep + 1}/{TUTOR_ONBOARDING_STEP_IDS.length}
          </div>
        </div>
        <div
          className="h-1 bg-white/[0.06]"
          role="progressbar"
          aria-label="Tutor onboarding progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <div
            className="h-full bg-[#FD954F] transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      <main
        className={`relative mx-auto px-4 pb-14 pt-8 sm:px-6 sm:pt-12 ${wide ? "max-w-6xl" : "max-w-2xl"}`}
        aria-live="polite"
      >
        {children}
      </main>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[#0F0F14] text-white">
      <Loader2 className="h-7 w-7 animate-spin text-orange-300" aria-label="Loading tutor tour" />
    </div>
  );
}

function StudioPreview({
  courses,
  selectedLanguageCode,
  selectedCefrLevel,
}: {
  courses: TutorStudioCourse[];
  selectedLanguageCode?: string;
  selectedCefrLevel?: string;
}) {
  const selectedCourse = courses.find((course) => course.languageCode === selectedLanguageCode);
  const otherCourses = courses.filter((course) => course.slug !== selectedCourse?.slug);
  const learnerRows = [
    {
      learner: "Camille L.",
      language: selectedCourse?.languageName || "French",
      level: selectedCefrLevel || "A2",
      sessions: "3 sessions",
      time: "28 min",
      lastActive: "Today",
      status: "On track",
      attention: false,
    },
    {
      learner: "Noor de V.",
      language: otherCourses[0]?.languageName || "Dutch",
      level: "A1",
      sessions: "No sessions",
      time: "This week",
      lastActive: "6 days ago",
      status: "Follow up",
      attention: true,
    },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-white/12 bg-[#121318]">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2.5">
          <BrandMark size={30} className="h-[30px] w-[30px] object-contain" alt="" />
          <div>
            <div className="text-sm font-black">Tutor Studio</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-orange-300">
              Preview
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[180px_minmax(0,1fr)]">
        <aside className="hidden border-r border-white/10 bg-[#0F0F14] p-3 lg:block">
          <div className="space-y-1">
            {[
              { label: "Overview", code: "01", active: false },
              { label: "Curriculum", code: "02", active: false },
              { label: "Student progress", code: "03", active: true },
            ].map((item) => (
              <div
                key={item.label}
                className={`flex items-center justify-between border-l-2 px-3 py-2.5 text-xs font-bold ${
                  item.active
                    ? "border-orange-300 bg-orange-500/[0.08] text-white"
                    : "border-transparent text-white/38"
                }`}
              >
                <span>{item.label}</span>
                <span className="font-mono text-[10px] text-white/25">{item.code}</span>
              </div>
            ))}
          </div>
        </aside>

        <div className="p-4 sm:p-6">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-orange-200">
                Last 7 days
              </p>
              <h2 className="mt-2 text-2xl font-black sm:text-3xl">Student progress</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/48">
                See who is practising, spot changes and open a student&apos;s full progress before
                your next lesson.
              </p>
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.12em] text-white/30">
              Sample data
            </span>
          </div>

          <section
            aria-label="Example progress summary"
            className="mt-5 grid grid-cols-2 border border-white/10 sm:grid-cols-4"
          >
            {[
              ["Approved", "2", "students with access"],
              ["Active", "1", "practised this week"],
              ["Sessions", "3", "28 min speaking"],
              ["Follow-up", "1", "may need attention"],
            ].map(([label, value, detail], index) => (
              <div
                key={label}
                className={`p-3 sm:p-4 ${index % 2 ? "border-l border-white/10" : ""} ${index > 1 ? "border-t border-white/10 sm:border-t-0" : ""} ${index > 0 ? "sm:border-l sm:border-white/10" : "sm:border-l-0"}`}
              >
                <div className="text-[9px] font-black uppercase tracking-[0.1em] text-white/32">
                  {label}
                </div>
                <div className="mt-3 text-2xl font-black tabular-nums text-white">{value}</div>
                <div className="mt-1 text-[10px] leading-4 text-white/35">{detail}</div>
              </div>
            ))}
          </section>

          <section className="mt-4" aria-labelledby="onboarding-approved-students">
            <div className="flex items-end justify-between gap-3">
              <h3 id="onboarding-approved-students" className="text-base font-black">
                Approved students
              </h3>
              <span className="text-xs font-bold text-white/35">Progress access only</span>
            </div>

            <div className="mt-3 overflow-hidden border border-white/10 bg-[#16171C]">
              <div className="hidden grid-cols-[minmax(140px,1.2fr)_minmax(110px,0.8fr)_minmax(130px,1fr)_100px_90px] border-b border-white/10 px-4 py-3 text-[9px] font-black uppercase tracking-[0.1em] text-white/30 md:grid">
                <span>Student</span>
                <span>Language</span>
                <span>Last 7 days</span>
                <span>Last active</span>
                <span>Status</span>
              </div>
              {learnerRows.map((learner) => (
                <div
                  key={learner.learner}
                  className="grid gap-3 border-b border-white/10 p-4 last:border-b-0 md:grid-cols-[minmax(140px,1.2fr)_minmax(110px,0.8fr)_minmax(130px,1fr)_100px_90px] md:items-center md:gap-0"
                >
                  <div>
                    <div className="text-sm font-black text-white">{learner.learner}</div>
                    <div className="mt-1 text-[10px] text-white/28 md:hidden">Approved access</div>
                  </div>
                  <div className="text-xs font-bold text-white/65">
                    {learner.language} <span className="text-white/30">{learner.level}</span>
                  </div>
                  <div>
                    <div className="text-xs font-black text-white/75">{learner.sessions}</div>
                    <div className="mt-1 text-[10px] text-white/30">{learner.time}</div>
                  </div>
                  <div className="text-xs font-bold text-white/48">{learner.lastActive}</div>
                  <div
                    className={`text-xs font-black ${learner.attention ? "text-orange-200" : "text-white/65"}`}
                  >
                    {learner.status}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export function TutorOnboardingExperience({ courses }: { courses: TutorStudioCourse[] }) {
  const { user, isLoading: authLoading } = useAuth();
  const router = useRouter();
  const posthog = usePostHog();
  const [state, setState] = useState<TutorOnboardingState>({
    currentStep: 0,
    answers: {},
    startedAt: Date.now(),
  });
  const [isHydrated, setIsHydrated] = useState(false);
  const [source, setSource] = useState("direct");
  const [languageSearch, setLanguageSearch] = useState("");
  const [isLanguagePickerOpen, setIsLanguagePickerOpen] = useState(false);
  const [isRegisteringTutor, setIsRegisteringTutor] = useState(false);
  const [registrationError, setRegistrationError] = useState("");
  const hasTrackedStart = useRef(false);
  const hasTrackedDemoStart = useRef(false);

  useEffect(() => {
    try {
      const stored = parseStoredTutorOnboardingState(localStorage.getItem(STORAGE_KEY));
      if (stored) setState(stored);
    } catch (caughtError) {
      logger.warn("Could not restore tutor onboarding state", { error: caughtError });
    } finally {
      const params = new URLSearchParams(window.location.search);
      setSource(normalizeTutorOnboardingSource(params.get("source")));
      setIsHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (caughtError) {
      logger.warn("Could not save tutor onboarding state", { error: caughtError });
    }
  }, [isHydrated, state]);

  useEffect(() => {
    if (!isHydrated || hasTrackedStart.current) return;
    hasTrackedStart.current = true;
    trackTutorEvent(posthog, "tutor_onboarding_started", {
      source,
      surface: "tutor_onboarding",
      total_steps: TUTOR_ONBOARDING_STEP_IDS.length,
      is_authenticated: Boolean(user),
    });
  }, [isHydrated, posthog, source, user]);

  const trackStepCompleted = useCallback(
    (stepIndex: number, properties: Record<string, unknown> = {}) => {
      trackTutorEvent(posthog, "tutor_onboarding_step_completed", {
        source,
        surface: "tutor_onboarding",
        step_id: TUTOR_ONBOARDING_STEP_IDS[stepIndex],
        step_number: stepIndex + 1,
        ...properties,
      });
    },
    [posthog, source]
  );

  const chooseAndContinue = (answers: Partial<TutorOnboardingAnswers>) => {
    const completedStep = state.currentStep;
    setState((current) => ({
      ...current,
      answers: { ...current.answers, ...answers },
      currentStep: Math.min(current.currentStep + 1, TUTOR_ONBOARDING_STEP_IDS.length - 1),
    }));
    trackStepCompleted(completedStep, {
      language: answers.languageCode || state.answers.languageCode,
      cefr_level: answers.cefrLevel || state.answers.cefrLevel,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToStep = (nextStep: number) => {
    setState((current) => ({
      ...current,
      currentStep: Math.max(0, Math.min(nextStep, TUTOR_ONBOARDING_STEP_IDS.length - 1)),
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => goToStep(state.currentStep - 1);

  const selectLanguage = (code: string, name: string) => {
    chooseAndContinue({ languageCode: code, languageName: name });
  };

  const continueFromConcept = () => {
    trackStepCompleted(0);
    goToStep(1);
  };

  const startDemo = () => {
    trackStepCompleted(3, {
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
      action: "start_demo",
    });
    goToStep(4);
  };

  const skipDemo = () => {
    trackStepCompleted(3, {
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
      action: "skip_demo",
    });
    trackStepCompleted(4, {
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
      completion_reason: "skipped",
    });
    trackTutorEvent(posthog, "tutor_demo_finished", {
      source,
      surface: "tutor_onboarding",
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
      completion_reason: "skipped",
      duration_seconds: 0,
    });
    goToStep(5);
  };

  const handleDemoStarted = useCallback(() => {
    if (hasTrackedDemoStart.current) return;
    hasTrackedDemoStart.current = true;
    trackTutorEvent(posthog, "tutor_demo_started", {
      source,
      surface: "tutor_onboarding",
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
    });
  }, [posthog, source, state.answers.cefrLevel, state.answers.languageCode]);

  const handleDemoFinished = useCallback(
    (reason: TutorDemoCompletionReason, durationSeconds: number) => {
      trackTutorEvent(posthog, "tutor_demo_finished", {
        source,
        surface: "tutor_onboarding",
        language: state.answers.languageCode,
        cefr_level: state.answers.cefrLevel,
        completion_reason: reason,
        duration_seconds: durationSeconds,
      });
      trackStepCompleted(4, {
        language: state.answers.languageCode,
        cefr_level: state.answers.cefrLevel,
        completion_reason: reason,
      });
      goToStep(5);
    },
    [posthog, source, state.answers.cefrLevel, state.answers.languageCode, trackStepCompleted]
  );

  const continueFromTutorStudio = () => {
    trackStepCompleted(5, {
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
    });
    goToStep(6);
  };

  const completeTutorOnboarding = () => {
    trackStepCompleted(6, {
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
      is_authenticated: true,
    });
    trackTutorEvent(posthog, "tutor_onboarding_completed", {
      source,
      surface: "tutor_onboarding",
      language: state.answers.languageCode,
      cefr_level: state.answers.cefrLevel,
      is_authenticated: true,
      seconds_to_studio: Math.max(0, Math.round((Date.now() - state.startedAt) / 1000)),
    });
    trackTutorEvent(posthog, "tutor_cta_clicked", {
      source,
      surface: "tutor_onboarding",
      placement: "signup_completed",
      destination: "/tutor-studio",
      is_authenticated: true,
    });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (caughtError) {
      logger.warn("Could not clear completed tutor onboarding state", { error: caughtError });
    }
    router.push("/tutor-studio?source=tutor_onboarding");
  };

  const enterTutorStudio = async () => {
    setIsRegisteringTutor(true);
    setRegistrationError("");
    try {
      await registerTutorAccount();
      completeTutorOnboarding();
    } catch (caughtError) {
      logger.error("Could not register signed-in user as a tutor", { error: caughtError });
      setRegistrationError("We could not finish setting up Tutor Studio. Please try again.");
    } finally {
      setIsRegisteringTutor(false);
    }
  };

  const selectedCourse = useMemo(
    () => courses.find((course) => course.languageCode === state.answers.languageCode),
    [courses, state.answers.languageCode]
  );

  if (!isHydrated) return <LoadingState />;

  if (
    state.currentStep === 4 &&
    state.answers.languageCode &&
    state.answers.languageName &&
    state.answers.cefrLevel
  ) {
    return (
      <TutorSpeakingDemo
        languageCode={state.answers.languageCode}
        languageName={state.answers.languageName}
        cefrLevel={state.answers.cefrLevel}
        onStarted={handleDemoStarted}
        onFinished={handleDemoFinished}
      />
    );
  }

  if (state.currentStep === 0) {
    const loop = [
      {
        label: "You teach",
        text: "Teach as you do now. Your material and judgement set the direction.",
        discipline: "Lesson",
      },
      {
        label: "They practise",
        text: "Give students a speaking task they can complete aloud between lessons.",
        discipline: "Homework",
      },
      {
        label: "You follow up",
        text: "Review approved activity and progress before the next lesson.",
        discipline: "Review",
      },
    ];

    return (
      <TutorOnboardingShell currentStep={0} wide>
        <div className="grid gap-8 motion-safe:animate-fade-in lg:grid-cols-[minmax(0,0.9fr)_minmax(520px,1.1fr)] lg:items-center lg:gap-14">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.16em] text-orange-300">
              Speaking homework for your students
            </p>
            <h1 className="mt-4 text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-6xl">
              Your lessons stay human.
              <br />
              <span className="text-[#FD954F]">The speaking keeps going.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-white/58 sm:text-lg">
              Turn material from your lessons into guided voice practice. Students speak between
              lessons; Tutor Studio helps you set the practice and review approved progress.
            </p>

            <div className="mt-8 flex items-end gap-5">
              <div className="w-full max-w-sm">
                <ContinueButton onClick={continueFromConcept}>
                  See the student experience
                </ContinueButton>
                <p className="mt-3 text-xs leading-5 text-white/35">
                  Then see how Tutor Studio connects that practice back to your lessons.
                </p>
              </div>
              <BrandMascot
                pose="teaching"
                priority
                className="hidden h-auto w-28 object-contain sm:block"
                sizes="112px"
                alt=""
              />
            </div>
          </div>

          <section className="overflow-hidden rounded-2xl border border-white/12 bg-[#121318]">
            <div className="border-b border-white/10 px-5 py-4 sm:px-6">
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-orange-300">
                Teaching loop
              </p>
              <p className="mt-1 text-sm font-bold text-white/70">Built around your lesson</p>
            </div>
            <div>
              {loop.map((item, index) => (
                <div
                  key={item.label}
                  className={`grid gap-3 px-5 py-5 sm:grid-cols-[44px_130px_minmax(0,1fr)] sm:items-start sm:px-6 ${index > 0 ? "border-t border-white/10" : ""}`}
                >
                  <span className="font-mono text-xs font-bold text-orange-300">0{index + 1}</span>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.12em] text-white/32">
                      {item.discipline}
                    </div>
                    <h2 className="mt-1 text-base font-black">{item.label}</h2>
                  </div>
                  <p className="text-sm leading-6 text-white/48">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-orange-300/25 bg-orange-500/[0.06] px-5 py-5 sm:px-6">
              <p className="text-sm font-black">Your teaching stays in charge.</p>
              <p className="mt-1.5 text-sm leading-6 text-white/48">
                You choose what to practise and what to revisit. Chicky gives students a responsive
                speaking partner when you are not in the room.
              </p>
            </div>
          </section>
        </div>
      </TutorOnboardingShell>
    );
  }

  if (state.currentStep === 1) {
    return (
      <TutorOnboardingShell currentStep={1} onBack={goBack} wide>
        <div className="grid gap-8 motion-safe:animate-fade-in lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-12">
          <div className="flex justify-center lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute inset-x-3 bottom-3 h-20 rounded-full bg-orange-400/12 blur-2xl" />
              <BrandMascot
                pose="teaching"
                priority
                className="relative h-auto w-[170px] object-contain sm:w-[220px] lg:w-[280px]"
                sizes="(max-width: 639px) 170px, (max-width: 1023px) 220px, 280px"
              />
            </div>
          </div>

          <div>
            <div className="text-center lg:text-left">
              <div className="text-xs font-black uppercase tracking-[0.16em] text-orange-300">
                Create a speaking homework demo
              </div>
              <h1 className="mt-5 text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl">
                Give students speaking practice
                <br />
                <span className="text-[#FD954F]">between lessons.</span>
              </h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-white/55 sm:text-lg lg:mx-0">
                Choose a language and level, then preview a short voice exercise from the student
                side before you offer speaking practice to learners.
              </p>
            </div>

            <div className="mt-8">
              <h2 className="text-center text-lg font-bold lg:text-left">
                Which language do your students practise?
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {POPULAR_TUTOR_LANGUAGES.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => selectLanguage(language.code, language.name)}
                    className="flex min-h-[66px] items-center gap-3 rounded-xl border border-white/10 bg-[#15161B] p-3 text-left transition hover:border-orange-300/45 hover:bg-white/[0.055] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    <span className="flex h-9 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/15 bg-white/[0.04] p-1.5">
                      <Image
                        src={language.flagSrc}
                        width={32}
                        height={22}
                        className="h-auto w-full rounded-[2px] object-cover"
                        data-testid={`language-flag-${language.code}`}
                        alt=""
                      />
                    </span>
                    <span className="font-bold">{language.shortName}</span>
                  </button>
                ))}
              </div>

              <ResponsiveLanguagePicker
                open={isLanguagePickerOpen}
                onOpenChange={setIsLanguagePickerOpen}
                trigger={
                  <button
                    type="button"
                    className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold text-orange-300 transition hover:bg-white/[0.035] hover:text-orange-200"
                  >
                    Browse all {Object.keys(LANGUAGE_OPTIONS).length} languages
                  </button>
                }
                languages={LANGUAGE_OPTIONS}
                selectedLanguageCode={state.answers.languageCode || ""}
                onSelectLanguage={(code) => {
                  const entry = Object.entries(LANGUAGE_OPTIONS).find(
                    ([, value]) => value === code
                  );
                  if (entry) selectLanguage(entry[1], entry[0]);
                }}
                searchTerm={languageSearch}
                onSearchTermChange={setLanguageSearch}
                title="Choose the language you teach"
              />
            </div>

            <div className="mt-5 grid grid-cols-2 border-t border-white/10 pt-5 text-xs font-semibold text-white/42">
              <span>
                <span className="mr-2 font-mono text-[10px] text-orange-300">01</span>
                Learner demo
              </span>
              <span>
                <span className="mr-2 font-mono text-[10px] text-orange-300">02</span>
                Tutor Studio preview
              </span>
            </div>
          </div>
        </div>
      </TutorOnboardingShell>
    );
  }

  if (state.currentStep === 2) {
    return (
      <TutorOnboardingShell currentStep={2} onBack={goBack} wide>
        <div className="grid gap-5 motion-safe:animate-fade-in lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start lg:gap-6">
          <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#121318] p-6 sm:p-7 lg:min-h-[430px]">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-orange-300">
              {state.answers.languageName} speaking homework
            </p>
            <h1 className="mt-4 text-3xl font-black leading-tight tracking-[-0.035em] sm:text-4xl">
              What level should this practice use?
            </h1>
            <p className="mt-4 text-base leading-7 text-white/55">
              Chicky adjusts the vocabulary, pace, and support so the exercise feels appropriate for
              the learner.
            </p>

            <div className="mt-8 border-t border-white/10 pt-5 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-white/38">Language</span>
                <span className="font-bold">{state.answers.languageName}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-4">
                <span className="text-white/38">Next</span>
                <span className="font-bold">Student voice demo</span>
              </div>
            </div>
          </section>

          <div className="grid gap-3 sm:grid-cols-2">
            {TUTOR_CEFR_OPTIONS.map((option) => (
              <OptionCard
                key={option.id}
                label={`${option.id} · ${option.label}`}
                description={option.description}
                shortcut={option.shortcut}
                selected={state.answers.cefrLevel === option.id}
                onClick={() => chooseAndContinue({ cefrLevel: option.id })}
              />
            ))}
          </div>
        </div>
      </TutorOnboardingShell>
    );
  }

  if (state.currentStep === 3) {
    return (
      <TutorOnboardingShell currentStep={3} onBack={goBack} wide>
        <div className="grid gap-5 motion-safe:animate-fade-in lg:grid-cols-[300px_minmax(0,1fr)] lg:items-stretch lg:gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] p-6 sm:p-7 lg:min-h-[520px]">
            <div className="relative z-10">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-300">
                Student homework demo
              </p>
              <h1 className="mt-3 max-w-[16ch] text-3xl font-black tracking-[-0.04em] sm:text-4xl">
                Try the speaking practice you can give students.
              </h1>
              <p className="mt-4 max-w-[34rem] text-sm leading-6 text-white/55 sm:text-base">
                This is guided voice homework. The student speaks in their own words, Chicky
                responds at the selected level, and the conversation keeps moving when they
                hesitate.
              </p>
            </div>

            <div className="pointer-events-none absolute -bottom-8 -right-7 hidden w-40 opacity-95 sm:block sm:w-48 lg:-bottom-6 lg:-right-8 lg:w-56">
              <div className="absolute inset-5 rounded-full bg-orange-400/10 blur-2xl" />
              <BrandMascot
                pose="teaching"
                className="relative h-auto w-full object-contain"
                sizes="(max-width: 639px) 160px, (max-width: 1023px) 192px, 224px"
                alt=""
              />
            </div>
          </section>

          <section
            className="overflow-hidden rounded-3xl border border-white/10 bg-[#141419] lg:self-start"
            aria-label="How the learner demo works"
          >
            <div className="grid grid-cols-2 xl:grid-cols-4">
              {[
                ["1", "Chicky starts", "A question gives the learner something concrete to say."],
                ["2", "The student speaks", "They answer aloud, in their own words."],
                ["3", "Chicky supports", "The conversation adapts and helps when they get stuck."],
                ["4", "They keep going", "Another question creates another chance to speak."],
              ].map(([number, label, text], index) => (
                <div
                  key={label}
                  className={`relative min-h-40 p-5 sm:p-6 ${index % 2 === 1 ? "border-l border-white/10" : ""} ${index > 1 ? "border-t border-white/10 xl:border-t-0" : ""} ${index > 0 ? "xl:border-l xl:border-white/10" : "xl:border-l-0"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/12 text-xs font-black text-orange-300">
                      {number}
                    </span>
                    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-white/20">
                      {index < 3 ? "continues" : "repeat"}
                    </span>
                  </div>
                  <h2 className="mt-5 text-sm font-black">{label}</h2>
                  <p className="mt-1.5 text-xs leading-5 text-white/42">{text}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 border-t border-white/10 sm:grid-cols-3">
              {[
                "Use the microphone exactly as a student would.",
                "Stay for one minute or the full five.",
                "Listen for how Chicky responds, supports, and continues.",
              ].map((item, index) => (
                <div
                  key={item}
                  className={`flex items-start gap-3 px-4 py-4 text-xs leading-5 text-white/62 ${index === 1 ? "border-l border-white/10" : ""} ${index === 2 ? "col-span-2 border-t border-white/10 sm:col-span-1 sm:border-l sm:border-t-0" : ""}`}
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-500/12 text-orange-300">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  {item}
                </div>
              ))}
            </div>

            <div className="grid gap-5 border-t border-orange-300/20 bg-orange-500/[0.08] p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(380px,0.9fr)] lg:items-center">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FD954F] text-[#1C1C1E]">
                  <Mic2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-orange-200">
                    Try this
                  </p>
                  <p className="mt-1 max-w-[42rem] text-sm font-bold leading-6 sm:text-base">
                    Speak in {state.answers.languageName}, then ask Chicky to practise something
                    from a recent lesson.
                  </p>
                </div>
              </div>

              <div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ContinueButton onClick={startDemo}>Try the student demo</ContinueButton>
                  <ContinueButton onClick={skipDemo} variant="secondary">
                    Skip to Tutor Studio
                  </ContinueButton>
                </div>
                <p className="mt-2 text-center text-[11px] leading-4 text-white/35">
                  Your browser will ask for microphone access. No account required.
                </p>
              </div>
            </div>
          </section>
        </div>
      </TutorOnboardingShell>
    );
  }

  if (state.currentStep === 5) {
    return (
      <TutorOnboardingShell currentStep={5} onBack={() => goToStep(3)} wide>
        <div className="motion-safe:animate-fade-in">
          <div className="grid gap-6 lg:grid-cols-[minmax(420px,0.9fr)_minmax(0,1.1fr)] lg:items-start">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-300">
                Tutor Studio
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-5xl">
                Choose what students practise and monitor their progress.
              </h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-white/55 sm:text-lg">
                Plan speaking practice and check progress before the next lesson.
              </p>
              <div className="mt-6 max-w-[250px]">
                <ContinueButton onClick={continueFromTutorStudio}>
                  Create tutor account
                </ContinueButton>
              </div>
            </div>

            <div
              role="list"
              aria-label="Tutor Studio capabilities"
              className="grid overflow-hidden rounded-2xl border border-white/10 sm:grid-cols-3"
            >
              {[
                [
                  "Choose a curriculum",
                  "Start with a complete course or create practice for your own lesson.",
                ],
                ["Pick lesson material", "Choose a starting chapter, level and teaching focus."],
                ["Monitor progress", "See who practised, for how long and what to revisit."],
              ].map(([label, text], index) => (
                <div
                  key={label}
                  role="listitem"
                  className={`bg-white/[0.02] p-4 ${index > 0 ? "border-t border-white/10 sm:border-l sm:border-t-0" : ""}`}
                >
                  <div className="text-sm font-black">{label}</div>
                  <p className="mt-2 text-[11px] leading-4 text-white/42">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <StudioPreview
              courses={courses}
              selectedLanguageCode={state.answers.languageCode}
              selectedCefrLevel={state.answers.cefrLevel}
            />
          </div>

          <div className="mt-5 grid overflow-hidden rounded-xl border border-white/10 sm:grid-cols-2">
            <div className="p-4 sm:p-5">
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-orange-300">
                Progress access
              </div>
              <h2 className="mt-2 text-sm font-black">Students choose to share progress</h2>
              <p className="mt-1 text-xs leading-5 text-white/40">
                You can only see a student&apos;s activity after they give you access.
              </p>
            </div>
            <div className="border-t border-white/10 p-4 sm:border-l sm:border-t-0 sm:p-5">
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-orange-300">
                What you can see
              </div>
              <h2 className="mt-2 text-sm font-black">Shared practice activity</h2>
              <p className="mt-1 text-xs leading-5 text-white/40">
                Review sessions, practice time and recent activity. Private conversations stay
                private.
              </p>
            </div>
          </div>
        </div>
      </TutorOnboardingShell>
    );
  }

  return (
    <TutorOnboardingShell currentStep={6} onBack={goBack} wide>
      <div className="grid gap-8 motion-safe:animate-fade-in lg:grid-cols-[minmax(0,1fr)_480px] lg:items-start lg:gap-10">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-300">
            Your tutor workspace
          </p>
          <h1 className="mt-3 text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-6xl">
            Offer speaking practice
            <br />
            <span className="text-[#FD954F]">after your next lesson.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-7 text-white/55 sm:text-lg">
            Create your tutor account to open Tutor Studio. Start with a course or flexible
            practice, share access with students, and review approved progress.
          </p>

          <div className="mt-7 grid gap-3 sm:grid-cols-3">
            {[
              ["Before", "Choose a course or shape practice around the lesson"],
              ["Between", "Students complete guided speaking practice with Chicky"],
              ["Next", "Review approved progress and plan your follow-up"],
            ].map(([label, text]) => (
              <div key={label} className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
                <div className="text-xs font-black uppercase tracking-[0.14em] text-orange-300">
                  {label}
                </div>
                <div className="mt-2 text-sm font-bold leading-6 text-white/72">{text}</div>
              </div>
            ))}
          </div>
        </div>

        {user ? (
          <div className="mx-auto w-full max-w-[360px] rounded-2xl border border-orange-300/20 bg-[#151714] p-5 sm:p-6">
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <BrandMark size={42} className="h-[42px] w-[42px] object-contain" alt="" />
              <div>
                <div className="text-sm font-black">Finish your tutor registration</div>
                <div className="text-xs text-white/35">Your signed-in account is ready</div>
              </div>
            </div>
            <p className="mt-5 text-sm leading-6 text-white/52">
              We will register this account for Tutor Studio. It does not invite students or share
              any practice.
            </p>
            <div className="mt-6">
              <ContinueButton
                onClick={enterTutorStudio}
                disabled={authLoading || isRegisteringTutor}
              >
                {isRegisteringTutor ? "Registering tutor account..." : "Register as a tutor"}
              </ContinueButton>
            </div>
            {registrationError && (
              <p className="mt-3 text-center text-xs leading-5 text-red-300">{registrationError}</p>
            )}
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[480px] rounded-2xl border border-orange-300/20 bg-[#121318] p-5 sm:p-6">
            <AuthForm
              intent="tutor"
              redirectPath="/tutor-studio?source=tutor_onboarding"
              variant="modal"
              headingLevel={2}
              onSuccess={completeTutorOnboarding}
            />
            <p className="mt-5 border-t border-white/10 pt-5 text-center text-xs leading-5 text-white/35">
              No card required. Signing up creates your Tutor Studio account. It does not invite
              students or share any practice.
            </p>
          </div>
        )}
      </div>
    </TutorOnboardingShell>
  );
}
